import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, LogOut, Star, ArrowLeft, GripVertical } from "lucide-react";

const empty = {
    name: "",
    description: "",
    price: 0,
    image_url: "",
    category: "essentials",
    featured: false,
    in_stock: true,
    sizes: ["S", "M", "L", "XL"],
    order: 100,
    split_front: true,
};

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | productObj
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("products"); // products | orders | settings
    const [dropSettings, setDropSettings] = useState({ next_drop_at: "", title: "", subtitle: "" });
    const [savingSettings, setSavingSettings] = useState(false);
    const [dragId, setDragId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem("em_admin_token");
        if (!token) {
            navigate("/admin");
            return;
        }
        load();
    }, [navigate]);

    const load = async () => {
        setLoading(true);
        try {
            const [p, t, s] = await Promise.all([
                api.get("/products"),
                api.get("/admin/transactions").catch(() => ({ data: [] })),
                api.get("/settings/next-drop").catch(() => ({ data: null })),
            ]);
            setProducts(p.data);
            setTransactions(t.data);
            if (s.data) {
                // Convert ISO datetime to local format for datetime-local input
                const iso = s.data.next_drop_at;
                const dt = new Date(iso);
                const pad = (n) => String(n).padStart(2, "0");
                const local = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
                setDropSettings({
                    next_drop_at: local,
                    title: s.data.title || "",
                    subtitle: s.data.subtitle || "",
                });
            }
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.removeItem("em_admin_token");
                navigate("/admin");
            }
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        setSavingSettings(true);
        try {
            const iso = new Date(dropSettings.next_drop_at).toISOString();
            await api.put("/settings/next-drop", {
                next_drop_at: iso,
                title: dropSettings.title,
                subtitle: dropSettings.subtitle,
            });
            toast.success("Drop settings updated.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSavingSettings(false);
        }
    };

    const openNew = () => {
        setForm(empty);
        setEditing("new");
    };

    const openEdit = (p) => {
        setForm({ ...p });
        setEditing(p);
    };

    const close = () => {
        setEditing(null);
        setForm(empty);
    };

    const onSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                price: parseFloat(form.price) || 0,
            };
            if (editing === "new") {
                await api.post("/products", payload);
                toast.success("Product created.");
            } else {
                await api.put(`/products/${editing.id}`, payload);
                toast.success("Product updated.");
            }
            close();
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (p) => {
        if (!window.confirm(`Delete "${p.name}"?`)) return;
        try {
            await api.delete(`/products/${p.id}`);
            toast.success("Deleted.");
            load();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    const onSyncCatalog = async () => {
        if (!window.confirm("Re-sync the canonical Energy Matcher catalog? This wipes existing products and re-seeds the official 5 with latest images & prices. Custom products you've added will be removed.")) return;
        try {
            const r = await api.post("/admin/reset-catalog");
            toast.success(`Catalog synced — ${r.data.seeded} products.`);
            load();
        } catch (err) {
            toast.error("Sync failed");
        }
    };

    const logout = () => {
        localStorage.removeItem("em_admin_token");
        navigate("/admin");
    };

    // --- Drag-to-reorder ---
    const onDragStart = (e, id) => {
        setDragId(id);
        e.dataTransfer.effectAllowed = "move";
        try {
            e.dataTransfer.setData("text/plain", id);
        } catch {}
    };
    const onDragOver = (e, id) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (id !== dragOverId) setDragOverId(id);
    };
    const onDragLeave = (id) => {
        if (id === dragOverId) setDragOverId(null);
    };
    const onDrop = async (e, targetId) => {
        e.preventDefault();
        const sourceId = dragId;
        setDragId(null);
        setDragOverId(null);
        if (!sourceId || sourceId === targetId) return;

        const next = [...products];
        const fromIdx = next.findIndex((p) => p.id === sourceId);
        const toIdx = next.findIndex((p) => p.id === targetId);
        if (fromIdx < 0 || toIdx < 0) return;
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);

        // Assign new sequential order values
        const updated = next.map((p, i) => ({ ...p, order: i + 1 }));
        setProducts(updated); // optimistic UI

        try {
            await api.post("/products/reorder", {
                items: updated.map((p) => ({ id: p.id, order: p.order })),
            });
            toast.success("Order saved.");
        } catch (err) {
            toast.error("Failed to save order — reverting.");
            load();
        }
    };

    return (
        <div
            data-testid="admin-dashboard"
            className="min-h-screen bg-[#0A0A0A] text-white"
        >
            {/* Header */}
            <header className="border-b border-white/10 sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-40">
                <div className="px-6 lg:px-12 h-16 flex items-center justify-between max-w-[1600px] mx-auto">
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-white inline-flex items-center gap-2"
                        >
                            <ArrowLeft size={12} /> Storefront
                        </Link>
                        <span className="text-muted-foreground/40">|</span>
                        <p className="font-mono text-xs uppercase tracking-[0.25em]">
                            EM // Admin Console
                        </p>
                    </div>
                    <button
                        data-testid="admin-logout"
                        onClick={logout}
                        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground hover:text-white"
                    >
                        <LogOut size={12} /> Logout
                    </button>
                </div>
            </header>

            <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
                    <div>
                        <p className="label-mono">— Control Room</p>
                        <h1 className="font-display text-5xl md:text-6xl uppercase tracking-tight leading-none mt-3">
                            Inventory
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex border border-white/15">
                            <button
                                data-testid="tab-products"
                                onClick={() => setTab("products")}
                                className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] ${tab === "products" ? "bg-white text-black" : "text-muted-foreground"}`}
                            >
                                Products ({products.length})
                            </button>
                            <button
                                data-testid="tab-orders"
                                onClick={() => setTab("orders")}
                                className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] ${tab === "orders" ? "bg-white text-black" : "text-muted-foreground"}`}
                            >
                                Orders ({transactions.length})
                            </button>
                            <button
                                data-testid="tab-settings"
                                onClick={() => setTab("settings")}
                                className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] ${tab === "settings" ? "bg-white text-black" : "text-muted-foreground"}`}
                            >
                                Drop
                            </button>
                        </div>
                        {tab === "products" && (
                            <div className="flex items-center gap-2">
                                <button
                                    data-testid="admin-sync-catalog"
                                    onClick={onSyncCatalog}
                                    className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white/5"
                                    title="Re-seed canonical Energy Matcher catalog (5 products with latest images & prices)"
                                >
                                    Sync Catalog
                                </button>
                                <button
                                    data-testid="admin-new-product"
                                    onClick={openNew}
                                    className="inline-flex items-center gap-2 bg-white text-black px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white/85"
                                >
                                    <Plus size={14} /> New Product
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {loading ? (
                    <p className="font-mono text-xs text-muted-foreground">Loading…</p>
                ) : tab === "products" ? (
                    <>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
                            Tip: drag any row by the handle to reorder. The product at #1 becomes the storefront hero card.
                        </p>
                        <div className="border border-white/10 overflow-x-auto">
                        <table className="w-full text-sm" data-testid="products-table">
                            <thead className="bg-white/5">
                                <tr className="text-left">
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-3 text-muted-foreground w-8"></th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">Image</th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">#</th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">Name</th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">Category</th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">Price</th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">Featured</th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">Stock</th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-right text-muted-foreground">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((p) => (
                                    <tr
                                        key={p.id}
                                        draggable
                                        onDragStart={(e) => onDragStart(e, p.id)}
                                        onDragOver={(e) => onDragOver(e, p.id)}
                                        onDragLeave={() => onDragLeave(p.id)}
                                        onDrop={(e) => onDrop(e, p.id)}
                                        className={`border-t border-white/10 hover:bg-white/5 transition-colors ${
                                            dragId === p.id ? "opacity-30" : ""
                                        } ${
                                            dragOverId === p.id && dragId !== p.id
                                                ? "border-t-2 border-t-white bg-white/10"
                                                : ""
                                        }`}
                                        data-testid={`row-${p.id}`}
                                    >
                                        <td
                                            className="px-2 py-3 text-muted-foreground cursor-grab active:cursor-grabbing"
                                            data-testid={`drag-handle-${p.id}`}
                                            aria-label="Drag to reorder"
                                            title="Drag to reorder"
                                        >
                                            <GripVertical size={14} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="w-14 h-14 bg-secondary overflow-hidden">
                                                <img
                                                    src={p.image_url}
                                                    alt={p.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground tabular-nums">
                                            {String(p.order ?? 100).padStart(3, "0")}
                                        </td>
                                        <td className="px-4 py-3 font-sans uppercase text-xs">
                                            {p.name}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            {p.category}
                                        </td>
                                        <td className="px-4 py-3 font-display text-lg">
                                            ${p.price.toFixed(2)}
                                        </td>
                                        <td className="px-4 py-3">
                                            {p.featured ? (
                                                <Star size={14} fill="currentColor" className="text-white" />
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-[10px] uppercase">
                                            {p.in_stock ? "In Stock" : "Sold Out"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    data-testid={`edit-${p.id}`}
                                                    onClick={() => openEdit(p)}
                                                    className="p-2 hover:bg-white/10"
                                                    aria-label="Edit"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    data-testid={`delete-${p.id}`}
                                                    onClick={() => onDelete(p)}
                                                    className="p-2 hover:bg-signal/20 text-signal"
                                                    aria-label="Delete"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {products.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="px-4 py-12 text-center text-muted-foreground font-mono text-xs uppercase tracking-[0.25em]">
                                            No products yet — create one.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    </>
                ) : tab === "orders" ? (
                    <div className="border border-white/10 overflow-x-auto" data-testid="orders-table">
                        <table className="w-full text-sm">
                            <thead className="bg-white/5">
                                <tr className="text-left">
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">Session</th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">Product</th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">Amount</th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">Status</th>
                                    <th className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground">Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((t) => (
                                    <tr key={t.session_id} className="border-t border-white/10">
                                        <td className="px-4 py-3 font-mono text-[10px] truncate max-w-[160px]" title={t.session_id}>
                                            {t.session_id?.slice(0, 20)}…
                                        </td>
                                        <td className="px-4 py-3 font-sans uppercase text-xs">{t.product_name}</td>
                                        <td className="px-4 py-3 font-display text-lg">${Number(t.amount).toFixed(2)}</td>
                                        <td className="px-4 py-3 font-mono text-[10px] uppercase">
                                            <span className={t.payment_status === "paid" ? "text-white" : "text-muted-foreground"}>
                                                {t.payment_status || t.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-[10px] text-muted-foreground">
                                            {new Date(t.created_at).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                                {transactions.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="px-4 py-12 text-center text-muted-foreground font-mono text-xs uppercase tracking-[0.25em]">
                                            No orders yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <form
                        onSubmit={saveSettings}
                        data-testid="drop-settings-form"
                        className="border border-white/10 p-8 max-w-2xl space-y-8"
                    >
                        <div>
                            <p className="label-mono">— Drop Settings</p>
                            <h2 className="font-display text-4xl uppercase tracking-tight mt-3">
                                Next Drop Countdown
                            </h2>
                            <p className="text-muted-foreground text-sm mt-2">
                                Controls the live countdown on the homepage.
                            </p>
                        </div>
                        <div>
                            <label className="label-mono block mb-2">Launch Date & Time (local)</label>
                            <input
                                type="datetime-local"
                                required
                                value={dropSettings.next_drop_at}
                                onChange={(e) => setDropSettings({ ...dropSettings, next_drop_at: e.target.value })}
                                data-testid="drop-next-at"
                                className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 outline-none font-mono text-sm"
                            />
                        </div>
                        <div>
                            <label className="label-mono block mb-2">Drop Title</label>
                            <input
                                value={dropSettings.title}
                                onChange={(e) => setDropSettings({ ...dropSettings, title: e.target.value })}
                                data-testid="drop-title"
                                className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 outline-none font-sans uppercase"
                                placeholder="Drop 04"
                            />
                        </div>
                        <div>
                            <label className="label-mono block mb-2">Subtitle</label>
                            <input
                                value={dropSettings.subtitle}
                                onChange={(e) => setDropSettings({ ...dropSettings, subtitle: e.target.value })}
                                data-testid="drop-subtitle"
                                className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 outline-none font-sans"
                                placeholder="Spring Capsule · Limited Run"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={savingSettings}
                            data-testid="drop-save"
                            className="bg-white text-black px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white/85 disabled:opacity-60"
                        >
                            {savingSettings ? "Saving…" : "Save Drop Settings"}
                        </button>
                    </form>
                )}
            </div>

            {/* Edit / Create Drawer */}
            {editing && (
                <div
                    data-testid="product-form-modal"
                    className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4"
                    onClick={close}
                >
                    <form
                        onSubmit={onSave}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-[#0A0A0A] border border-white/15 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0A0A0A]">
                            <p className="font-mono text-xs uppercase tracking-[0.25em]">
                                {editing === "new" ? "New Product" : "Edit Product"}
                            </p>
                            <button
                                type="button"
                                onClick={close}
                                data-testid="form-close"
                                className="p-1 hover:bg-white/10"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div>
                                <label className="label-mono block mb-2">Name</label>
                                <input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="form-name"
                                    className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 outline-none font-sans uppercase"
                                />
                            </div>
                            <div>
                                <label className="label-mono block mb-2">Description</label>
                                <textarea
                                    required
                                    rows={3}
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    data-testid="form-description"
                                    className="w-full bg-transparent border border-white/15 focus:border-white p-3 outline-none font-sans text-sm resize-none"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="label-mono block mb-2">Price (USD)</label>
                                    <input
                                        required
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={form.price}
                                        onChange={(e) => setForm({ ...form, price: e.target.value })}
                                        data-testid="form-price"
                                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 outline-none font-display text-2xl"
                                    />
                                </div>
                                <div>
                                    <label className="label-mono block mb-2">Category</label>
                                    <input
                                        value={form.category}
                                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                                        data-testid="form-category"
                                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 outline-none font-sans uppercase"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="label-mono block mb-2">
                                    Display Order <span className="text-muted-foreground">(lower = shown first; #1 becomes the hero card)</span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={form.order}
                                    onChange={(e) => setForm({ ...form, order: e.target.value })}
                                    data-testid="form-order"
                                    className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 outline-none font-display text-2xl tabular-nums"
                                />
                            </div>
                            <div>
                                <label className="label-mono block mb-2">Image URL</label>
                                <input
                                    required
                                    value={form.image_url}
                                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                                    data-testid="form-image"
                                    className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 outline-none font-mono text-xs"
                                />
                                {form.image_url && (
                                    <div className="mt-3 aspect-video bg-secondary overflow-hidden border border-white/10">
                                        <img
                                            src={form.image_url}
                                            alt="preview"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-6">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.featured}
                                        onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                                        data-testid="form-featured"
                                        className="w-4 h-4 accent-white"
                                    />
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Featured on home</span>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.in_stock}
                                        onChange={(e) => setForm({ ...form, in_stock: e.target.checked })}
                                        data-testid="form-in-stock"
                                        className="w-4 h-4 accent-white"
                                    />
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em]">In stock</span>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer" title="Check if the product image shows front + back side-by-side. The 360° turn will reveal the back when rotated.">
                                    <input
                                        type="checkbox"
                                        checked={form.split_front !== false}
                                        onChange={(e) => setForm({ ...form, split_front: e.target.checked })}
                                        data-testid="form-split-front"
                                        className="w-4 h-4 accent-white"
                                    />
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Front + back photo</span>
                                </label>
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-3 sticky bottom-0 bg-[#0A0A0A]">
                            <button
                                type="button"
                                onClick={close}
                                className="px-4 py-2 border border-white/15 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white/5"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                data-testid="form-submit"
                                className="bg-white text-black px-6 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white/85 disabled:opacity-60"
                            >
                                {saving ? "Saving…" : editing === "new" ? "Create" : "Save Changes"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
